// CRM layer — the opportunities board (added Phase 6-C).
//
// The central screen for every opportunity across all clients. Ported from the
// preview harness (backend/crm/preview/public/app.js board view) into React.
//
// Two equally-weighted views behind a toggle:
//   Kanban — one column per active stage; the exits (not_serious / lost) sit in
//            a collapsed area underneath so closed work is visible but not noisy.
//   List   — a sortable, filterable table of exactly the same rows.
//
// Filters map straight onto GET /api/opportunities query params, so the server
// does the filtering and the client only groups/sorts.

import React, { useCallback, useEffect, useState } from 'react';
import { FiGrid, FiList, FiPlus, FiSearch, FiRefreshCw, FiBell, FiHelpCircle } from 'react-icons/fi';
import {
  crmListOpportunities, crmGetTypes, crmNav,
  STAGE_LABEL, STAGE_RANK, LADDER, EXIT_STAGES, CLOSED_STAGES,
  stagePillClass, fmtHours, isPendingClassification, typeLabel, PENDING_STAGE
} from './crmApi';
import useAgentNames from './useAgentNames';
import CrmStartOpportunityModal from './CrmStartOpportunityModal';

// Calm identity (Batch 6): the per-stage rail colour now comes from the central
// tokens via a `s-<stage>` class on the column (crm.css) instead of a literal.

// Age thresholds are unchanged (72h / 24h) — only the rendering is now a small
// soft token pill instead of an inline colour pair.
const agePillClass = (h) => (h >= 72
  ? 'crm-pill crm-pill-danger'
  : h >= 24 ? 'crm-pill crm-pill-warn' : 'crm-pill crm-pill-neutral');

const CrmOpportunitiesBoard = ({ setActivePage }) => {
  const { nameOf } = useAgentNames();
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem('crmBoardView') || 'kanban');
  const [closedOpen, setClosedOpen] = useState(false);
  const [sort, setSort] = useState({ key: 'hoursInStage', dir: 'desc' });
  const [filters, setFilters] = useState({ stage: '', type: '', assigned_to: '', department: '', phone: '' });
  const [phoneInput, setPhoneInput] = useState('');

  // "فرصة جديدة": step 1 asks for the number, step 2 is the normal start flow.
  const [askPhone, setAskPhone] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [startFor, setStartFor] = useState(null);

  // One-shot confirmation left behind by the classification gate (read-and-clear).
  const [flash, setFlash] = useState('');
  useEffect(() => {
    const m = crmNav.takeFlash();
    if (!m) return;
    setFlash(m);
    const t = setTimeout(() => setFlash(''), 4000);
    return () => clearTimeout(t);
  }, []);

  const setView2 = (v) => { setView(v); localStorage.setItem('crmBoardView', v); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await crmListOpportunities(params);
      setRows(res.data.opportunities);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { crmGetTypes().then((r) => setTypes(r.data.types)).catch(() => {}); }, []);

  const open = (id) => { crmNav.setOpportunity(id); setActivePage('crm-opportunity'); };

  // Filter options come from the data itself plus the type catalogue, so an
  // agent (who cannot list users) still gets a working agent filter.
  const agentOptions = [...new Set(rows.map((o) => o.assigned_to_extension).filter(Boolean))].sort();
  const deptOptions = [...new Set([
    ...types.map((t) => t.department).filter(Boolean),
    ...rows.map((o) => o.department).filter(Boolean)
  ])].sort();

  // Batch 3: pending rows come back from GET / alongside everything else (the
  // server filters on "not closed", and a pre-stage row is not closed). They
  // are split out here so they are neither counted as pipeline volume nor lost
  // from the kanban — the LADDER columns below only cover real stages, so
  // without their own strip these cards would render nowhere at all.
  const pending = rows.filter(isPendingClassification);
  const active = rows.filter((o) => !CLOSED_STAGES.includes(o.crm_stage) && !isPendingClassification(o));
  const closed = rows.filter((o) => EXIT_STAGES.includes(o.crm_stage));
  const chased = active.filter((o) => o.needsAttention).length;
  const orphans = active.filter((o) => !o.assigned_to_extension).length;
  const stale = active.filter((o) => o.hoursInStage >= 72).length;

  const setF = (k, v) => setFilters({ ...filters, [k]: v });
  const anyFilter = Object.values(filters).some(Boolean);

  // ---- card -----------------------------------------------------------------
  const Card = ({ o }) => (
    <div onClick={() => open(o.id)} title={o.request_details || ''}
      className={`crm-oppcard${o.needsAttention ? ' attn' : ''}`}>
      <div className="ttl">{typeLabel(o.opportunity_type)}</div>
      <div className="who">
        {o.client_name || 'عميل غير مسجل'}{' '}
        <span className="ph">{o.phone_number}</span>
      </div>
      {o.request_details && (
        <div className="det">{o.request_details}</div>
      )}
      <div className="meta">
        {/* Batch 3: first pill on the card, so an untriaged row is obvious
            wherever the card appears (strip, closed drawer, filtered view). */}
        {isPendingClassification(o) && (
          <span className="crm-pill crm-pill-pending">بانتظار التصنيف</span>
        )}
        {o.has_unread && (
          <span className="crm-unread" title="تعليق داخلي جديد"><span className="crm-unread-dot" /> تعليق جديد</span>
        )}
        {o.needsAttention && (
          <span className="crm-pill crm-pill-warn">
            <FiBell size={10} /> {o.reminder_count}
          </span>
        )}
        <span className={agePillClass(o.hoursInStage)}>{fmtHours(o.hoursInStage)}</span>
        {o.department && <span className="crm-pill crm-pill-neutral">{o.department}</span>}
        <span className="crm-spacer" />
        <span className={`crm-pill ${o.assigned_to_extension ? 'crm-pill-accent' : 'crm-pill-danger'}`}>
          {nameOf(o.assigned_to_extension) || 'غير مسند'}
        </span>
      </div>
    </div>
  );

  // ---- list -----------------------------------------------------------------
  const sorted = [...rows].sort((a, b) => {
    const val = (o) => {
      if (sort.key === 'stage') return STAGE_RANK[o.crm_stage] ?? 9;
      if (sort.key === 'client') return o.client_name || '';
      if (sort.key === 'type') return o.opportunity_type;
      if (sort.key === 'agent') return o.assigned_to_extension || '';
      if (sort.key === 'department') return o.department || '';
      return o.hoursInStage;
    };
    const [x, y] = [val(a), val(b)];
    const cmp = typeof x === 'number' ? x - y : String(x).localeCompare(String(y), 'ar');
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  const th = (k, label) => (
    <th className="sortable"
      onClick={() => setSort({ key: k, dir: sort.key === k && sort.dir === 'desc' ? 'asc' : 'desc' })}>
      {label}{sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  return (
    // Batch 6 (Group A): calm/enterprise identity via the central tokens.
    // Styling scope only — views, filters, sorting, navigation and the
    // "فرصة جديدة" flow are untouched.
    <div className="crm-scope crm-calm crm-calm-page">
      <div className="page-header">
        <h1 className="page-title">الفرص</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="crm-count">
            {rows.length} فرصة · {active.length} نشطة
            {stale > 0 && <span className="crm-txt-danger"> · {stale} متأخرة</span>}
          </span>
          <button className="btn btn-outline btn-sm" onClick={load}><FiRefreshCw size={14} /> تحديث</button>
          <button className="btn btn-primary" onClick={() => { setNewPhone(''); setAskPhone(true); }}>
            <FiPlus size={14} /> فرصة جديدة
          </button>
        </div>
      </div>

      {/* Batch 3: confirmation handed over from the classification gate, which
          navigates here the moment an archiving answer is given. */}
      {flash && <div className="crm-flash">{flash}</div>}

      {/* KPI strip — the four original counts plus the classification backlog */}
      <div className="crm-kpis">
        {[['نشطة', active.length, 'accent'], ['بانتظار التصنيف', pending.length, pending.length ? 'accent' : 'mute'],
          ['تحتاج متابعة', chased, chased ? 'warn' : 'mute'],
          ['غير مسندة', orphans, orphans ? 'danger' : 'mute'],
          ['تم التعاقد', rows.filter((o) => o.crm_stage === 'contracted').length, 'success']]
          .map(([l, n, c]) => (
            <div key={l} className="crm-kpi">
              <div className={`n ${c}`}>{n}</div>
              <div className="l">{l}</div>
            </div>
          ))}
      </div>

      {/* toolbar */}
      <div className="crm-toolbar">
        <div style={{ position: 'relative' }}>
          <FiSearch className="crm-searchico" />
          <input className="filter-input" style={{ paddingRight: 32, width: 190 }} placeholder="بحث برقم الجوال..."
            value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setF('phone', phoneInput); }} />
        </div>
        <select className="filter-select" value={filters.stage} onChange={(e) => setF('stage', e.target.value)}>
          <option value="">كل المراحل</option>
          {[PENDING_STAGE, ...LADDER, ...EXIT_STAGES].map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
        </select>
        <select className="filter-select" value={filters.type} onChange={(e) => setF('type', e.target.value)}>
          <option value="">كل الأنواع</option>
          {types.map((t) => <option key={t.id} value={t.type_name}>{t.type_name}</option>)}
        </select>
        <select className="filter-select" value={filters.assigned_to} onChange={(e) => setF('assigned_to', e.target.value)}>
          <option value="">كل الموظفين</option>
          {agentOptions.map((a) => <option key={a} value={a}>{nameOf(a)}</option>)}
        </select>
        <select className="filter-select" value={filters.department} onChange={(e) => setF('department', e.target.value)}>
          <option value="">كل الأقسام</option>
          {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        {anyFilter && (
          <button className="btn btn-outline btn-sm"
            onClick={() => { setFilters({ stage: '', type: '', assigned_to: '', department: '', phone: '' }); setPhoneInput(''); }}>
            مسح الفلاتر
          </button>
        )}
        <span style={{ flex: 1 }} />
        <button className={`btn btn-sm ${view === 'kanban' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setView2('kanban')}>
          <FiGrid size={14} /> لوحة
        </button>
        <button className={`btn btn-sm ${view === 'list' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setView2('list')}>
          <FiList size={14} /> جدول
        </button>
      </div>

      {/* ═══════ THE CLASSIFICATION BACKLOG ═══════
          BELOW the toolbar on purpose: it reads as a result OF the filters
          rather than a banner above them, which is what makes per-agent triage
          work — pick an agent and the strip narrows to that agent's untriaged
          calls, so a manager can hand one person their own backlog.
          `rows` arrives already filtered by the server (load() forwards every
          active filter as a query param), so `pending` inherits the agent,
          department, type and phone filters for free.
          There is deliberately NO `!filters.stage` guard: filtering to
          "بانتظار التصنيف" used to HIDE this strip, which is precisely
          backwards. Any other stage filter returns no pending rows at all, so
          the strip hides itself. Hidden when empty so it never becomes
          furniture. */}
      {pending.length > 0 && (
        <div className="crm-pending-strip">
          <div className="sh">
            <FiHelpCircle size={15} style={{ color: 'var(--crm-accent-strong)' }} />
            <h3>بانتظار التصنيف ({pending.length})</h3>
            <span className="sub">
              {anyFilter
                ? 'مكالمات غير مصنّفة ضمن الفلاتر المحددة — حدّد نوع كل تواصل ليدخل مساره الصحيح.'
                : 'مكالمات أنشأها النظام تلقائياً — حدّد نوع كل تواصل ليدخل مساره الصحيح.'}
            </span>
          </div>
          <div className="crm-pending-cards">
            {pending.map((o) => <Card key={o.id} o={o} />)}
          </div>
        </div>
      )}

      {loading ? (
        <div className="table-container"><div className="crm-empty">جاري التحميل...</div></div>
      ) : view === 'kanban' ? (
        <>
          <div className="crm-kan">
            {LADDER.map((stage) => {
              const items = active.filter((o) => o.crm_stage === stage);
              return (
                <div key={stage} className={`crm-kan-col s-${stage}`}>
                  <div className="crm-kan-head">
                    <span className="nm">{STAGE_LABEL[stage]}</span>
                    <span className="crm-pill crm-pill-neutral">{items.length}</span>
                    <span className="rail" />
                  </div>
                  <div className="crm-kan-body">
                    {items.length === 0
                      ? <div className="crm-kan-empty">لا توجد فرص</div>
                      : items.map((o) => <Card key={o.id} o={o} />)}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 18 }}>
            <button className="btn btn-outline" style={{ width: '100%', justifyContent: 'flex-start' }}
              onClick={() => setClosedOpen(!closedOpen)}>
              {closedOpen ? '▼' : '◀'} الطلبات المغلقة (غير جاد / خسارة) — {closed.length}
            </button>
            {closedOpen && (
              <div className="crm-kan-closed">
                {closed.length === 0
                  ? <div className="crm-faint" style={{ fontSize: 13 }}>لا توجد طلبات مغلقة</div>
                  : closed.map((o) => <Card key={o.id} o={o} />)}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="table-container">
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead><tr>
                {th('client', 'العميل')}{th('type', 'نوع الطلب')}{th('stage', 'المرحلة')}
                {th('agent', 'الموظف')}{th('department', 'القسم')}{th('hoursInStage', 'مدة المرحلة')}
                <th>التذكيرات</th><th>التفاصيل</th>
              </tr></thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr><td colSpan="8" className="crm-empty">لا توجد نتائج مطابقة</td></tr>
                ) : sorted.map((o) => (
                  <tr key={o.id} className="crm-rowlink" onClick={() => open(o.id)}>
                    <td><strong>{o.client_name || 'غير مسجل'}</strong><br />
                      <span className="crm-faint" style={{ fontSize: 11, direction: 'ltr', display: 'inline-block' }}>{o.phone_number}</span></td>
                    <td>{typeLabel(o.opportunity_type)}{o.has_unread && <span className="crm-unread" title="تعليق داخلي جديد" style={{ marginRight: 6 }}><span className="crm-unread-dot" /> تعليق جديد</span>}</td>
                    <td><span className={stagePillClass(o.crm_stage)}>{STAGE_LABEL[o.crm_stage]}</span></td>
                    <td>{nameOf(o.assigned_to_extension) || <span className="crm-txt-danger">غير مسند</span>}</td>
                    <td>{o.department || '—'}</td>
                    <td><span className={agePillClass(o.hoursInStage)}>{fmtHours(o.hoursInStage)}</span></td>
                    <td>{o.needsAttention
                      ? <span className="crm-pill crm-pill-warn">{o.reminder_count}</span>
                      : (o.reminder_count || 0)}</td>
                    <td className="crm-muted" style={{ fontSize: 12, maxWidth: 260 }}>{(o.request_details || '—').slice(0, 80)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* step 1 of "فرصة جديدة": which client? */}
      {askPhone && (
        <div className="modal-overlay" onClick={() => setAskPhone(false)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>فرصة جديدة</h2>
              <button className="modal-close" onClick={() => setAskPhone(false)}>×</button>
            </div>
            <p className="crm-page-sub" style={{ marginBottom: 14 }}>
              أدخل رقم جوال العميل. سيتحقق النظام من وجود طلبات نشطة له، ويقترح نوع الطلب.
            </p>
            <input className="filter-input" style={{ width: '100%' }} autoFocus placeholder="05xxxxxxxx"
              value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && newPhone.trim()) { setAskPhone(false); setStartFor(newPhone.trim()); } }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary" disabled={!newPhone.trim()}
                onClick={() => { setAskPhone(false); setStartFor(newPhone.trim()); }}>متابعة</button>
              <button className="btn btn-outline" onClick={() => setAskPhone(false)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}

      {/* step 2: the existing start flow (suggestion + create) */}
      {startFor && (
        <CrmStartOpportunityModal
          phoneNumber={startFor}
          onClose={() => setStartFor(null)}
          onCreated={() => { setStartFor(null); load(); }}
          onOpenOpportunity={open}
        />
      )}
    </div>
  );
};

export default CrmOpportunitiesBoard;
